<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { capitalize } from "lodash";

  import type { HappyHourDealTier } from "@shared/types/Deals";
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { CartType } from "@shared/types/Cart";

  export let distributorId: number;
  export let tier: HappyHourDealTier;
  export let nextDealTier: HappyHourDealTier | null;
  export let isRip: boolean;

  const getDealQuantity = (cart: CartType, tier: HappyHourDealTier): number => {
    if (!cart?.orders?.length) return 0;
    const order = cart.orders.find(o => o.distributor.id === distributorId);

    if (!order?.products?.length) return 0;
    const inventoryIds = tier.products.map(p => p.inventory_id);
    const applicableProducts: CartProduct[] = order.products.filter(p =>
      inventoryIds.includes(p.inventory_id),
    );

    if (!applicableProducts.length) return 0;

    if (tier.tierUom === "CASE") {
      function sumCaseQuantity(sum: number, p: CartProduct): number {
        const casesFromUnits = p.product.container_case_size
          ? Math.floor(p.unit_quantity / p.product.container_case_size)
          : 0;
        return sum + p.case_quantity + casesFromUnits;
      }
      return applicableProducts.reduce(sumCaseQuantity, 0);
    } else {
      function sumUnitQuantity(sum: number, p: CartProduct): number {
        const unitsFromCases = (p.product.container_case_size || 0) * p.case_quantity;
        return sum + p.unit_quantity + unitsFromCases;
      }
      return applicableProducts.reduce(sumUnitQuantity, 0);
    }
  };

  const getContainerLabel = (containerType: string, total: number): string => {
    if (tier.tierUom === "CASE") {
      return pluralize("Case", total);
    } else {
      return pluralize(capitalize(containerType), total);
    }
  };

  $: quantity = getDealQuantity($CartStore, tier);
  $: total = tier.tier;
  $: containerType = tier.products[0].container_type;
  $: dealSatisfied = quantity >= total;
  $: progress = (quantity / total) * 100;
  $: containerLabel = getContainerLabel(containerType, total);
</script>

<style>
  @property --progress {
    syntax: "<percentage>";
    initial-value: 0%;
    inherits: true;
  }

  .fizz-progress-tracker {
    transition: --progress 0.4s ease-out;
  }
</style>

<div class="fizz-text-strong" style="min-width: 160px;" data-testid="deal-progress-bar">
  {#if dealSatisfied}
    <p class="fizz-text-deal fizz-text-align-right fizz-flex-row-4 fizz-align-content-start">
      <Icon width="16" height="16" type="DealSatisfied" className="fizz-icon-16 fizz-text-deal" />
      <span>
        {quantity}/{total}&nbsp;{containerLabel} &ndash;
        {#if isRip}
          RIP qualified!
        {:else}
          Deal applied!
        {/if}
      </span>
    </p>
    {#if nextDealTier}
      <p class="fizz-text-subdued">
        Next deal tier available &ndash; {nextDealTier.maxDiscountLabel}
      </p>
    {/if}
  {:else}
    <div class="align-center">
      <p>
        {quantity}/{total}&nbsp;{containerLabel}
      </p>
      <div class="fizz-progress-tracker" style="--progress: {progress}%;" />
    </div>
  {/if}
</div>
